import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/DashboardLayout";
import CockpitHero from "../../components/CockpitHero";
import ConferenceHealthCard from "../../components/ConferenceHealthCard";
import DiploksWalletSummaryCard from "../../components/DiploksWalletSummaryCard";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import {
  Users,
  CalendarRange,
  Award,
  Wallet,
  ScrollText,
  ArrowUpRight,
  Activity,
  Building2,
} from "lucide-react";

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [tombstones, setTombstones] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setStats(r.data));
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/orgs?include_archived=true").then((r) => setOrgs(r.data));
    // Tombstones are super_admin-only on the backend.  Gate client-side
    // to silence a benign 403 on every org_admin dashboard render.
    if (user?.role === "super_admin") {
      api.get("/orgs/tombstones").then((r) => setTombstones(r.data || [])).catch(() => {});
    }
  }, [user?.role]);

  const archivedOrgCount = orgs.filter((o) => o.archived).length;
  const activeOrgCount = orgs.filter((o) => !o.archived).length;
  const isSuper = user?.role === "super_admin";
  const roleLabel = isSuper ? "Diplomexa Command" : "Organiser Console";

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <CockpitHero
          testId="overview-hero"
          eyebrow={roleLabel}
          title={`Good ${timeOfDay()},`}
          titleAccent={user?.name?.split(" ")[0] || "Operator"}
          titleTail="."
          description={`Here's the pulse of ${isSuper ? "the platform" : "your organisation"} right now.`}
          primaryCta={{
            label: "Manage events",
            to: "/dashboard/events",
            icon: ArrowUpRight,
            testid: "manage-events-button",
          }}
          secondaryCta={isSuper ? {
            label: "View organisations",
            to: "/dashboard/organizations",
            testid: "manage-orgs-button",
          } : null}
        />

        {/* Stat cards */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Building2} label={isSuper ? "Organisations" : "Events"} value={isSuper ? stats?.orgs : stats?.events} sub={isSuper ? "White-label clients" : "All editions"} />
          <StatCard icon={CalendarRange} label={isSuper ? "Events" : "Registrations"} value={isSuper ? stats?.events : stats?.registrations} sub={isSuper ? "Platform-wide" : "Across events"} />
          <StatCard icon={Users} label="Paid delegates" value={stats?.paid_registrations} sub="Mock-paid flow" />
          <StatCard icon={Wallet} label="Revenue" value={`₹${(stats?.revenue || 0).toLocaleString()}`} sub="Lifetime" gold />
        </div>

        {/* Content + Chart */}
        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
                      Registrations per event
                    </p>
                    <h3 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">
                      Performance mix
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-emerald-600 font-semibold">
                    <Activity className="h-3.5 w-3.5" /> LIVE
                  </div>
                </div>
                <div className="mt-6 h-72 min-h-[280px]">
                  {stats?.per_event?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={stats.per_event}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="event" tick={{ fontSize: 11, fill: "#64748b" }} />
                        <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                        <Tooltip
                          contentStyle={{
                            background: "#0A192F",
                            border: "1px solid #D4AF37",
                            color: "#fff",
                            fontSize: 12,
                          }}
                        />
                        <Bar dataKey="registrations" radius={[6, 6, 0, 0]}>
                          {stats.per_event.map((_, i) => (
                            <Cell key={i} fill={i % 2 === 0 ? "#0F4C81" : "#D4AF37"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-slate-400">
                      Loading chart…
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6 bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold tracking-tight text-[#0A192F]">
                    {isSuper ? "All events" : "Your events"}
                  </h3>
                  <span className="text-xs text-slate-500">{events.length} total</span>
                </div>
                <div className="mt-5 divide-y divide-slate-100">
                  {events.map((ev) => (
                    <Link
                      to={`/dashboard/events/${ev.id}`}
                      key={ev.id}
                      className="flex items-center justify-between py-4 hover:bg-slate-50/80 -mx-6 px-6 transition-colors"
                      data-testid={`event-row-${ev.id}`}
                    >
                      <div>
                        <p className="text-sm font-semibold text-[#0A192F]">{ev.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {new Date(ev.start_date).toLocaleDateString()} · {ev.location} · {ev.format}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge status={ev.status} />
                        <ArrowUpRight className="h-4 w-4 text-slate-400" />
                      </div>
                    </Link>
                  ))}
                  {events.length === 0 && (
                    <p className="py-8 text-sm text-slate-500 text-center">No events yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-4 space-y-6">
            {/* Diploks wallet summary — visible for org_admin / event_manager / school_admin */}
            <DiploksWalletSummaryCard />
            {/* Phase D — Conference Health Score widget for the most recent active event */}
            {events.length > 0 && (
              <ConferenceHealthCard
                eventId={events.find((e) => e.status === "published")?.id || events[0]?.id}
                eventTitle={events.find((e) => e.status === "published")?.title || events[0]?.title}
              />
            )}
            {isSuper && (
              <Card className="bg-[#0A192F] text-white border-[#D4AF37]/20">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                        White-label clients
                      </p>
                      <h3 className="mt-2 text-xl font-semibold tracking-tight">Organisations</h3>
                    </div>
                    <Link
                      to="/dashboard/organizations"
                      className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white"
                      data-testid="open-orgs-link"
                    >
                      Manage →
                    </Link>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-md bg-white/5 px-2 py-2.5">
                      <p className="font-serif text-2xl text-emerald-400" data-testid="dash-orgs-active">{activeOrgCount}</p>
                      <p className="text-[9px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">Active</p>
                    </div>
                    <div className="rounded-md bg-white/5 px-2 py-2.5">
                      <p className="font-serif text-2xl text-amber-400" data-testid="dash-orgs-archived">{archivedOrgCount}</p>
                      <p className="text-[9px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">Archived</p>
                    </div>
                    <div className="rounded-md bg-white/5 px-2 py-2.5">
                      <p className="font-serif text-2xl text-rose-400" data-testid="dash-orgs-deleted">{tombstones.length}</p>
                      <p className="text-[9px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">Deleted</p>
                    </div>
                  </div>
                  <div className="mt-5 space-y-3">
                    {orgs.slice(0, 5).map((o) => (
                      <div
                        key={o.id}
                        className={`flex items-center gap-3 p-3 rounded-md bg-white/5 ${o.archived ? "opacity-60" : ""}`}
                        data-testid={`dash-org-${o.slug}`}
                      >
                        <div className="h-9 w-9 rounded-md bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] font-bold text-sm">
                          {o.name[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold truncate flex items-center gap-2">
                            {o.name}
                            {o.archived && (
                              <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300">
                                Archived
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400 truncate">{o.plan} plan</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {tombstones.length > 0 && (
                    <Link
                      to="/dashboard/organizations"
                      className="mt-4 flex items-center justify-between p-3 rounded-md border border-rose-400/20 bg-rose-400/5 hover:bg-rose-400/10 transition-colors"
                      data-testid="tombstones-cta"
                    >
                      <p className="text-xs text-rose-200">
                        {tombstones.length} deleted organisation{tombstones.length === 1 ? "" : "s"} on record
                      </p>
                      <span className="text-[10px] uppercase tracking-[0.22em] text-rose-300">View →</span>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
                  Platform quick links
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <QuickLink to="/dashboard/analytics" icon={BarChart3Icon} label="Analytics" />
                  <QuickLink to="/dashboard/crm" icon={MessageIcon} label="Delegate CRM" />
                  <QuickLink to="/dashboard/settings" icon={Award} label="Branding" />
                  <QuickLink to="/dashboard/certificates" icon={ScrollText} label="Certificates" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function timeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function Badge({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-600",
    published: "bg-emerald-100 text-emerald-700",
    completed: "bg-[#0A192F] text-[#D4AF37]",
    archived: "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, gold }) {
  return (
    <div
      className={`p-5 rounded-xl border ${
        gold
          ? "bg-[#0A192F] text-white border-[#D4AF37]/30"
          : "bg-white border-slate-200"
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-[10px] uppercase tracking-[0.22em] font-bold ${gold ? "text-[#D4AF37]" : "text-slate-500"}`}>
            {label}
          </p>
          <p className={`mt-3 font-serif text-3xl ${gold ? "text-[#D4AF37]" : "text-[#0A192F]"}`}>
            {value ?? "—"}
          </p>
          <p className={`text-xs mt-1 ${gold ? "text-slate-400" : "text-slate-500"}`}>{sub}</p>
        </div>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${
          gold ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-[#0F4C81]/10 text-[#0F4C81]"
        }`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 px-3 py-3 rounded-md bg-slate-50 border border-slate-200 hover:border-[#D4AF37] transition-colors text-sm font-semibold text-[#0A192F]"
      data-testid={`quick-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon className="h-4 w-4 text-[#0F4C81]" />
      {label}
    </Link>
  );
}

function BarChart3Icon(props) { return <Activity {...props} />; }
function MessageIcon(props) { return <Users {...props} />; }
